import {useEffect, useRef} from 'react'

import {noop, twMerge} from '@sdox/utils'

import {useHorizontalDragScroll} from '../../'
import {LazyImage} from '../LazyImage'
import {Scrim} from '../Media/Scrim'

import type {ButtonProps} from '../Button'

interface ThumbnailProps extends ButtonProps {
  src: string
  index?: number
  isActive?: boolean
  isGrabbing?: boolean
}

const Thumbnail = ({index = 0, src, onClick = noop, isActive = false, isGrabbing = false}: ThumbnailProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    buttonRef.current?.scrollIntoView({behavior: 'smooth'})
  }, [isActive])

  return (
    <button
      ref={buttonRef}
      aria-label={`Thumbnail ${index}`}
      className={twMerge(
        'group relative mx-spacing-8 inline-block',
        isActive && 'ring-1 ring-interactive-outline-default-default',
        isGrabbing && 'cursor-grabbing',
      )}
      onClick={onClick}
    >
      <Scrim />
      <LazyImage
        className={'aspect-[16/9] h-spacing-56 select-none object-cover'}
        src={src}
      />
    </button>
  )
}

export interface ThumbnailsProps {
  images?: string[]
  imageIndex?: number
  onClick?: (index: number) => void
}

export const Thumbnails = ({images = [], imageIndex = 0, onClick = noop}: ThumbnailsProps) => {
  const {isGrabbing, handlers} = useHorizontalDragScroll()

  return (
    <div
      className={twMerge(
        'hide-scrollbar touch-none overflow-x-scroll',
        isGrabbing ? 'cursor-grabbing' : 'cursor-grab',
      )}
      {...handlers}
    >
      <nav className={'whitespace-nowrap p-spacing-8 text-center'}>
        {images.map((src, index) => (
          <Thumbnail
            key={index}
            index={index}
            src={src}
            isActive={index === imageIndex}
            isGrabbing={isGrabbing}
            onClick={() => onClick(index)}
          />
        ))}
      </nav>
    </div>
  )
}
